import { type ApiClient, createApiClient, isApiErrorBody, type PublicConfig } from '@akpl/shared';

/** Where the API lives. Only a build-time default; the app config comes from `/v1/config`. */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? 'http://localhost:8787').replace(
  /\/$/,
  '',
);

type TokenProvider = () => Promise<string | null>;

let tokenProvider: TokenProvider = async () => null;

/** Installed once Firebase Auth is ready (src/auth). */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

/**
 * The operator's current Firebase ID token. `createApiClient` adds it to every typed call; this
 * is for the handful of raw `fetch`es the console makes (the Worker-proxied upload, lib/upload).
 */
export function getAuthToken(): Promise<string | null> {
  return Promise.resolve(tokenProvider());
}

const IS_TEST = import.meta.env.MODE === 'test';

/** Typed client for every CMS screen; carries the operator's Firebase ID token. */
export const api: ApiClient = createApiClient({
  baseUrl: API_BASE,
  getToken: () => tokenProvider(),
  // Resolve `fetch` per call (tests stub the global after this module is evaluated).
  fetch: (input, init) => globalThis.fetch(input, init),
  validateResponses: import.meta.env.DEV,
  retries: IS_TEST ? 0 : 3,
  retryBaseMs: IS_TEST ? 0 : 300,
  timeoutMs: IS_TEST ? 2000 : 15_000,
});

export async function fetchPublicConfig(signal?: AbortSignal): Promise<PublicConfig> {
  const res = await fetch(`${API_BASE}/v1/config`, { signal });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const message = isApiErrorBody(body) ? body.error.message : `HTTP ${res.status}`;
    throw new Error(message);
  }
  return body as PublicConfig;
}
